import refreshTokenMutation from '@/apollo/mutations/auth/refreshToken.gql'
import { Context, NuxtAppOptions } from '@nuxt/types'
import { Inject } from '@nuxt/types/app'
import { DocumentNode } from 'graphql'
import { AuthGetterTypes, AuthMutationTypes } from '~/store/account/auth'
import {
  GraphqlMutationResponse,
  GraphqlQueryOptions,
  GraphqlQueryResponse
} from '~/types/apolloTypes'

// Extend NuxtAppOptions to include SSR request-scoped cache
declare module '@nuxt/types' {
  interface NuxtAppOptions {
    // SSR request-scoped flags to prevent redundant operations
    __ssrTokenRefreshed?: boolean
    __ssrTokenRefreshPromise?: Promise<void>
    __ssrCsrfToken?: string
  }
}

declare module 'vuex/types/index' {
  // skipcq: JS-0387, JS-0356
  interface Store<S> {
    $fetchGraphqlDataFromCache(
      query: DocumentNode,
      variables: any
    ): GraphqlQueryResponse['data'] | null
    $fetchGraphqlData(
      query: DocumentNode,
      variables: any,
      refetch?: boolean,
      refreshToken?: boolean
    ): any
    $applyGraphqlMutation(
      mutation: DocumentNode,
      variables: any,
      refetchQueries?: any,
      refreshToken?: boolean
    ): any
    $getGQLAfter(pageNumber: number, limit: number): string
    $clearGqlStore(): void
  }
}

declare module '@nuxt/types' {
  interface NuxtAppOptions {
    $fetchGraphqlDataFromCache(
      query: DocumentNode,
      variables: any
    ): GraphqlQueryResponse['data'] | null
    $fetchGraphqlData(
      query: DocumentNode,
      variables: any,
      refetch?: boolean,
      refreshToken?: boolean
    ): any
    $applyGraphqlMutation(
      mutation: DocumentNode,
      variables: any,
      refetchQueries?: any,
      refreshToken?: boolean
    ): any
    $getGQLAfter(pageNumber: number, limit: number): string
    $clearGqlStore(): void
  }
  interface Context {
    $fetchGraphqlDataFromCache(
      query: DocumentNode,
      variables: any
    ): GraphqlQueryResponse['data'] | null
    $fetchGraphqlData(
      query: DocumentNode,
      variables: any,
      refetch?: boolean,
      refreshToken?: boolean
    ): any
    $applyGraphqlMutation(
      mutation: DocumentNode,
      variables: any,
      refetchQueries?: any,
      refreshToken?: boolean
    ): any
    $getGQLAfter(pageNumber: number, limit: number): string
    $clearGqlStore(): void
  }
}

declare module 'vue/types/vue' {
  interface Vue {
    $fetchGraphqlDataFromCache(
      query: DocumentNode,
      variables: any
    ): GraphqlQueryResponse['data'] | null
    $fetchGraphqlData(
      query: DocumentNode,
      variables: any,
      refetch?: boolean,
      refreshToken?: boolean
    ): any
    $applyGraphqlMutation(
      mutation: DocumentNode,
      variables: any,
      refetchQueries?: any,
      refreshToken?: boolean
    ): any
    $getGQLAfter(pageNumber: number, limit: number): string
    $clearGqlStore(): void
  }
}

export const getCsrfPath = (config: Context['$config']): string => {
  if (process.server) {
    if (!config.csrfServerUri) {
      throw new Error('CSRF server URI is not configured')
    }
    return config.csrfServerUri as string
  }
  if (process.client) {
    if (!config.csrfClientUri) {
      throw new Error('CSRF client URI is not configured')
    }
    return config.csrfClientUri as string
  }
  throw new Error('Both process.server and process.client are false')
}

const parseCookieString = (str: string): string => {
  if (!str) {
    return ''
  }
  const value = str.split(';')[0].split('=')[1]
  if (!value) {
    return ''
  }
  return value
}

const getCSRFHeaders = async (
  app: NuxtAppOptions
): Promise<Record<string, Record<string, string>>> => {
  const { $cookies, $config } = app

  // Check request-scoped cache first (SSR optimization)
  if (process.server && app.__ssrCsrfToken) {
    return {
      headers: {
        'X-CSRFToken': app.__ssrCsrfToken,
        Cookie: `csrftoken=${app.__ssrCsrfToken}`
      }
    }
  }

  let csrfToken = $cookies.get('csrftoken')

  if (!csrfToken) {
    const response = await fetch(getCsrfPath($config), {
      credentials: 'include'
    })

    if (process.client) {
      csrfToken = $cookies.get('csrftoken')
    } else {
      const cookieString = response.headers.get('set-cookie') as string
      csrfToken = parseCookieString(cookieString)
    }
  }

  // Cache for this SSR request
  if (process.server && csrfToken) {
    app.__ssrCsrfToken = csrfToken
  }

  return {
    headers: {
      'X-CSRFToken': csrfToken as string,
      Cookie: `csrftoken=${csrfToken as string}`
    }
  }
}

const getContext = async (app: NuxtAppOptions): Promise<Record<string, Record<string, string>>> => {
  const { $cookies, store } = app

  // the CSRF headers are always required (uses request-scoped cache on SSR)
  const context = await getCSRFHeaders(app)

  // The JWT is managed in Vuex
  const token = store?.getters[`account/auth/${AuthGetterTypes.TOKEN}`] as string
  const refreshToken = $cookies.get('JWT-refresh-token') as string

  if (!process.client && refreshToken) {
    // On client the refresh token is passed with every request
    // On server we transparently pass the cookie
    context.headers.Cookie += `; JWT-refresh-token=${refreshToken}`
  }

  if (token) {
    context.headers.Authorization = `JWT ${token}`
  }

  return context
}

const refreshIfTokenExpired = async (app: NuxtAppOptions): Promise<void> => {
  // SSR optimization: skip if token was already refreshed in this request
  // This prevents redundant refresh calls when multiple GraphQL queries run during SSR
  if (process.server && app.__ssrTokenRefreshed) {
    return
  }

  // If there's already a refresh in progress, wait for it instead of starting another
  if (process.server && app.__ssrTokenRefreshPromise) {
    await app.__ssrTokenRefreshPromise
    return
  }

  if (app.store) {
    const now = (new Date().getTime() + 30_000) / 1000
    const isLoggedIn = app.store.getters[`account/auth/${AuthGetterTypes.GET_LOGGED_IN}`]
    const expiry = app.store.getters[`account/auth/${AuthGetterTypes.EXPIRY}`]
    if (isLoggedIn && now > expiry) {
      const client = app.apolloProvider?.defaultClient

      if (client) {
        // Create the refresh promise and store it for deduplication
        const refreshPromise = (async () => {
          const context = await getContext(app)
          const params = { mutation: refreshTokenMutation, variables: {}, context }
          // don't call the action directly unless you have a thing for infinite loops
          try {
            const response = await client.mutate(params)
            if (app.store) {
              app.store.commit(
                `account/auth/${AuthMutationTypes.SET_LOGGED_IN}`,
                response.data.refreshToken.token
              )
            }
          } catch (e) {
            // Let it silently fail
          }
        })()

        if (process.server) {
          app.__ssrTokenRefreshPromise = refreshPromise
        }

        await refreshPromise

        // Mark as refreshed for this SSR request and clean up the promise reference
        if (process.server) {
          app.__ssrTokenRefreshed = true
          app.__ssrTokenRefreshPromise = undefined
        }
      }
    } else if (process.server) {
      // Token is still valid, mark as "checked" to skip future checks in this request
      app.__ssrTokenRefreshed = true
    }
  }
}

/**
 * Get the `after` value that can be used in a GQL query depending on the
 * page number and the limit provided.
 *
 * The `after` attribute is a special base64 encoded value
 * depending on the end cursor value of the query.
 *
 * @param {number} pageNumber
 * @param {number} limit
 * @return {string}
 */
export const getGQLAfter = (pageNumber: number, limit: number): string => {
  if (!pageNumber) return '' // return empty for 0 or null
  const endCursor = (pageNumber - 1) * limit - 1
  if (isNaN(endCursor)) return ''
  if (pageNumber !== 1) {
    return btoa(`arrayconnection:${endCursor}`)
  }
  return ''
}

export default ({ app }: { app: NuxtAppOptions }, inject: Inject): void => {
  inject(
    'fetchGraphqlDataFromCache',
    (query: DocumentNode, variables: any): GraphqlQueryResponse['data'] | null => {
      if (!app.apolloProvider || !app.apolloProvider.defaultClient) {
        return null
      }
      try {
        const client = app.apolloProvider.defaultClient
        return client.readQuery<GraphqlQueryResponse['data']>({ query, variables })
      } catch (e) {
        // Silently fail on error, Apollo throws an error if data is not found in cache.
        return null
      }
    }
  )
  inject(
    'fetchGraphqlData',
    async (
      query: DocumentNode,
      variables: any,
      refetch = false,
      refreshToken = true
    ): Promise<GraphqlQueryResponse> => {
      /**
       * Abstracts graphql client code from actions.
       * Used to fetch data through queries.
       */
      if (!app.apolloProvider || !app.apolloProvider.defaultClient) {
        throw new Error("Something went wrong.  We're on it!")
      }

      if (refreshToken) {
        await refreshIfTokenExpired(app)
      }

      const context = await getContext(app)

      const params = { query, variables, context } as GraphqlQueryOptions
      if (refetch) {
        params.fetchPolicy = 'network-only'
      }

      const client = app.apolloProvider.defaultClient
      return client.query(params)
    }
  )

  inject(
    'applyGraphqlMutation',
    async (
      mutation: DocumentNode,
      variables: any,
      refetchQueries?: any,
      refreshToken = true
    ): Promise<GraphqlMutationResponse> => {
      /**
       * Abstracts graphql client code from actions.
       * Used to apply graphql mutations.
       */

      if (!app.apolloProvider || !app.apolloProvider.defaultClient) {
        throw new Error("Something went wrong.  We're on it!")
      }

      if (refreshToken) {
        await refreshIfTokenExpired(app)
      }

      const context = await getContext(app)
      return app.apolloProvider.defaultClient.mutate({
        mutation,
        variables,
        context,
        refetchQueries,
        awaitRefetchQueries: true
      })
    }
  )

  inject('clearGqlStore', () => {
    const client = app.apolloProvider?.defaultClient
    if (!client) return

    // Prevent Apollo from rejecting in-flight queries with InvariantError(16)
    // during store reset. These queries are irrelevant after logout.
    const queryManager = (client as any).queryManager
    queryManager?.fetchQueryRejectFns?.clear()

    client.clearStore()
  })

  inject('getGQLAfter', getGQLAfter)
}
